/*
 * Date rendering for the app.
 *
 * The backend stores ISO-8601 UTC strings. The UI was slicing them
 * (`created_at?.slice(0, 10)`), which threw away the time AND ignored the
 * viewer's timezone — an event at 23:40 UTC displayed as the previous day for
 * anyone west of Greenwich. These helpers convert properly and keep one
 * format across every table.
 */

const pad = (n) => String(n).padStart(2, "0");

function parse(iso) {
  if (!iso) return null;
  // A bare "YYYY-MM-DDTHH:MM:SS" with no zone is UTC on the server; without
  // the marker some browsers parse it as local time and shift it silently.
  const s = typeof iso === "string" && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? `${iso}Z` : iso;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-09-05 14:52" — sortable, unambiguous, in the viewer's timezone. */
export function formatDateTime(iso) {
  const d = parse(iso);
  if (!d) return "—";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** With seconds, for the audit trail where ordering within a minute matters. */
export function formatDateTimeSeconds(iso) {
  const d = parse(iso);
  if (!d) return "—";
  return `${formatDateTime(iso)}:${pad(d.getSeconds())}`;
}

/** "3h ago" — a relative hint to sit beside the absolute value, never instead of it. */
export function timeAgo(iso) {
  const d = parse(iso);
  if (!d) return "";
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 0) return "just now";
  const steps = [[60, "s"], [60, "m"], [24, "h"], [7, "d"], [4.35, "w"], [12, "mo"]];
  let value = secs;
  let unit = "s";
  for (const [size, label] of steps) {
    if (value < size) { unit = label; break; }
    value = value / size;
    unit = label;
  }
  if (unit === "s") return "just now";
  return `${Math.floor(value)}${unit} ago`;
}

/** Full precision + timezone, for a title tooltip on a shortened value. */
export function fullTimestamp(iso) {
  const d = parse(iso);
  return d ? d.toString() : "";
}
